import { useForm } from '@tanstack/react-form'
import { useNavigate, useParams } from '@tanstack/react-router'
import { InvalidTokenMessage } from '../components/InvalidTokenMessage'
import mccLogo from '@/assets/mcc_logo.png'
import { Container } from '@/components/Container'
import LoadingButton from '@/components/custom/loading-button'
import PasswordInput from '@/components/custom/password-input'
import { Spinner } from '@/components/Spinner'
import {
  useCheckSetupTokenQuery,
  useResetPasswordMutation,
} from '@/features/auth/hooks'
import { ResetPasswordSchema } from '@/features/auth/schema/passwordSchema'
import { resetPasswordRoute } from '@/routes'

export default function ResetPassword() {
  const navigate = useNavigate()
  const resetToken = useParams({ from: resetPasswordRoute.id }).tempToken || ''

  const { mutate: resetPasswordMutation, isPending: isResetPending } =
    useResetPasswordMutation()
  const { error, isLoading: isChecking } = useCheckSetupTokenQuery(
    resetToken,
    'reset',
  )

  const form = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      await resetPasswordMutation({
        token: resetToken,
        newPassword: value.newPassword,
      })
      navigate({ to: '/login' })
    },
  })

  if (isChecking) {
    return (
      <Container>
        <Spinner size={48} />
      </Container>
    )
  }

  // Show error if token is expired or invalid

  if (error) {
    return <InvalidTokenMessage error={error} type="reset" />
  }

  return (
    <Container>
      <div className="flex flex-col items-center gap-6">
        <img
          src={mccLogo}
          alt="Maranatha Christian Church"
          className="w-48 sm:w-52"
        />
        <div className="w-full max-w-sm rounded-lg border bg-white p-6 shadow-sm">
          <h1 className="mb-4 text-xl font-semibold">Reset Password</h1>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-3"
          >
            <form.Field
              name="newPassword"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    ResetPasswordSchema.innerType().shape.newPassword.safeParse(
                      value,
                    )
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => (
                <div className="gap-0">
                  <PasswordInput
                    placeholder="••••••••"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="pr-10"
                    showStrengthIndicator
                    field={field}
                    label="New Password"
                  />
                </div>
              )}
            />
            <form.Field
              name="confirmPassword"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    ResetPasswordSchema.innerType().shape.confirmPassword.safeParse(
                      value,
                    )
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => (
                <div className="gap-0 mt-6 mb-8">
                  <PasswordInput
                    placeholder="••••••••"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="pr-10"
                    field={field}
                    label="Confirm Password"
                  />
                </div>
              )}
            />
            <form.Subscribe
              selector={(state) => [state.canSubmit]}
              children={([canSubmit]) => (
                <LoadingButton
                  type="submit"
                  className="w-full"
                  isLoading={isResetPending}
                  disabled={!canSubmit || isResetPending}
                >
                  {isResetPending ? 'Resetting...' : 'Reset Password'}
                </LoadingButton>
              )}
            />
          </form>
        </div>
      </div>
    </Container>
  )
}
